import * as THREE from 'three';
import gsap from 'gsap';
import useThreeBase from '~/clinic-inside/utils/useThreeBase';
import {
  type Text3d,
  type Text3dOptions,
  use3dText,
} from '~/clinic-inside/utils/use3dText';
import { useClinicModel } from '~/clinic-inside/utils/useClinicModel';
import type { ContentSceneFragment } from '#gql';
import { useVideo } from '~/clinic-inside/utils/useVideo';

export type ThreeContext = {
  scene: THREE.Scene;
  text3d: Text3d;
  addVisibilityObject: (
    mesh: THREE.Object3D,
    offset: number,
    range?: { front: number; back: number },
    visibleAtStart?: boolean,
  ) => void;
};

export type Vector3 = {
  x: number;
  y: number;
  z: number;
};

export type AnimationState = {
  cameraPosition: Vector3;
  cameraRotation: Vector3;
};

export type POCVersion = 'desktop' | 'mobile';

export const PositionY = 1.35;
export const PositionX = 0;

export interface Clinic3dOptions {
  debug?: boolean;
  textOffset?: THREE.Vector3 | undefined;
  textColor?: string | undefined;
  parallaxEnabled?: boolean;
  parallaxStrength?: number;
  resolution?: number;
  maxFPS?: number;
}

export async function useClinic3d(
  canvasContainer: HTMLElement,
  canvas: HTMLCanvasElement,
  scrollContainer: HTMLElement,
  version: POCVersion,
  content: ContentSceneFragment[],
  options?: Clinic3dOptions,
) {
  const { progress } = useClinic3dProgress();

  const state = ref<AnimationState>({
    cameraPosition: { x: 0, y: 0, z: 0 },
    cameraRotation: { x: 0, y: 0, z: 0 },
  });

  if (typeof window !== 'undefined') {
    (window as any).Clinic3d = (window as any).Clinic3d || {
      LoadingProgress: {
        GlbModel: 0,
      },
    };
  }

  const { isVs, isVl } = useViewportSize();

  const threeBase = useThreeBase(
    canvasContainer,
    canvas,
    7,
    '#e8edf7',
    options?.resolution ?? 1,
    options?.debug,
    options?.textColor,
    options?.parallaxEnabled,
    options?.parallaxStrength,
    options?.maxFPS ?? 60,
  );

  const textOptions: Text3dOptions = {
    textOffset: options?.textOffset,
    textColor: options?.textColor,
  };
  const text3d = await use3dText(textOptions);

  const threeContext = {
    addVisibilityObject: threeBase.addVisibilityObject,
    scene: threeBase.scene,
    text3d,
  };

  addEnvironmentPlanes();

  addClinic();

  gsap.set(threeBase.camera.position, {
    x: 0,
    y: PositionY,
    z: 7,
  });

  const scrollTl = gsap.timeline({
    paused: true,
    ease: 'none',
    defaults: {
      ease: 'none',
    },
    scrollTrigger: {
      trigger: scrollContainer,
      scrub: 0.4,
      pin: true,
      end: '1000%',
      onUpdate: function () {
        state.value.cameraPosition.x = threeBase.cameraTargetPosition.x;
        state.value.cameraPosition.y = threeBase.cameraTargetPosition.y;
        state.value.cameraPosition.z = threeBase.cameraTargetPosition.z;
        onScrollUpdate(state.value.cameraPosition as THREE.Vector3);
        progress.value = scrollTl.progress();
      },
    },
  });

  scrollTl
    .to(threeBase.cameraTargetPosition, {
      x: 0,
      y: PositionY,
      z: -70,
      duration: 0.94,
      ease: 'none',
    })
    .to(threeBase.cameraTargetPosition, {
      x: -2,
      y: PositionY,
      z: -72,
      duration: 0.06,
      ease: 'none',
    })
    .to(
      threeBase.cameraTargetRotation,
      {
        x: 0,
        y: -Math.PI / 2,
        z: 0,
        duration: 0.06,
        ease: 'none',
      },
      '<',
    );

  const textTl = gsap.timeline({
    paused: true,
    ease: 'none',
    defaults: {
      ease: 'none',
    },
    scrollTrigger: {
      trigger: scrollContainer,
      scrub: 0.4,
      end: '1000%',
      start: '-20%',
    },
  });

  if (isVs.value) {
    addTextCardsAnimation();
  }

  textTl.fromTo(
    '.clinic-3d .texts',
    {
      visibility: 'visible',
    },
    {
      visibility: 'hidden',
    },
  );

  const videoPositions = [
    new THREE.Vector3(2.75, PositionY + 0.7, -6.85),
    new THREE.Vector3(2.75, PositionY + 0.7, -6.85 - 14.15),
    new THREE.Vector3(2.75, PositionY + 0.7, -35),
    new THREE.Vector3(2.75, PositionY + 0.7, -49.1),
    new THREE.Vector3(2.75, PositionY + 0.7, -63.25),
    new THREE.Vector3(0 - 1.1, PositionY + 0.7, -77.1),
  ];

  // videos are only display inside 3d-context on media --vl
  if (content[0]?.video && content[0].video.video) {
    for (let i = 0; i < content.length; i++) {
      const video = content[i].video?.video;
      const thumbnail = content[i].thumbnail?.responsiveImage?.src;

      if (video) {
        const { plane, button, play } = useVideo(
          video,
          thumbnail || '',
          threeBase,
          i + 1,
        );

        plane.position.set(
          videoPositions[i].x,
          videoPositions[i].y,
          videoPositions[i].z,
        );

        button.position.set(
          videoPositions[i].x,
          videoPositions[i].y,
          videoPositions[i].z + 0.1,
        );

        plane.userData.isHoveredTimeline = { play: () => {} };
        plane.userData.isHoveredTimeline.play = (isHovered: boolean) => {
          const timeline = gsap.timeline({
            paused: true,
          });
          timeline.fromTo(
            button.scale,
            {
              x: isHovered ? 1 : 1.2,
              y: isHovered ? 1 : 1.2,
            },
            {
              x: isHovered ? 1.2 : 1,
              y: isHovered ? 1.2 : 1,
              duration: 0.3,
              ease: 'power2.inOut',
            },
          );
          timeline.play();
        };

        // make video hoverable on media --vl
        if (isVl.value) {
          threeBase.addRaycasterObject(plane);
        }

        // only show video on proximity
        threeBase.addVisibilityObject(button, 10, { front: 7, back: -4 });

        button.userData.toggleVisibleTimeline = { play: () => {} };

        button.userData.toggleVisibleTimeline.play = (visible: boolean) => {
          const timeline = gsap.timeline({
            paused: true,
          });
          timeline.fromTo(
            button.scale,
            {
              x: visible ? 0 : 1,
              y: visible ? 0 : 1,
            },
            {
              x: visible ? 1 : 0,
              y: visible ? 1 : 0,
              duration: 1,
              ease: 'power2.inOut',
            },
          );
          timeline.play();
        };

        threeBase.scene.add(plane);
        // only show button on media --vl
        if (isVl.value) {
          threeBase.scene.add(button);
        }

        plane.userData.onclick = () => {
          if (
            document.documentElement.getAttribute('data-experience') ===
            'running'
          ) {
            threeBase.lastCameraPosition.copy(threeBase.camera.position);
            threeBase.cameraTargetPosition.set(
              plane.position.x,
              plane.position.y,
              plane.position.z + 2.75,
            );
            play();
          }
        };
      }
    }
  }

  return {
    start: threeBase.startTick,
    stop: threeBase.stopTick,
    threeBase,
  };

  function onScrollUpdate(newCameraPosition: THREE.Vector3) {
    threeBase.updateVisibleObjects(newCameraPosition);
  }

  async function addEnvironmentPlanes() {
    const woodsTexture = await threeBase.textureLoader.loadAsync(
      '/images/panorama-small.avif',
    );
    woodsTexture.colorSpace = THREE.SRGBColorSpace;
    woodsTexture.wrapS = THREE.RepeatWrapping;
    woodsTexture.wrapT = THREE.RepeatWrapping;
    woodsTexture.repeat.set(1, 1);

    const textureHeight = 6;

    const planeGeometry = new THREE.PlaneGeometry(
      (textureHeight / woodsTexture.image.naturalHeight) *
        woodsTexture.image.naturalWidth,
      textureHeight,
      512,
      512,
    );

    const planeMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      map: woodsTexture,
    });

    const planeRight = new THREE.Mesh(planeGeometry, planeMaterial);
    planeRight.position.set(10, 2, -58);
    planeRight.rotation.set(0, Math.PI * -0.5, 0);
    threeBase.scene.add(planeRight);

    const planeLeft = new THREE.Mesh(planeGeometry, planeMaterial);
    planeLeft.position.set(10.1, 2, -15);
    planeLeft.rotation.set(0, Math.PI * -0.5, 0);
    threeBase.scene.add(planeLeft);
  }

  function addTextCardsAnimation() {
    const textCards = Array.from(
      document.querySelectorAll('.clinic-3d .texts .card'),
    );

    for (let i = 0; i < textCards.length; i++) {
      const cPrev = textCards[i - 1];
      const c = textCards[i];

      let h = 900;
      if (typeof window !== 'undefined') {
        h = window.innerHeight;
      }

      gsap.set(c, {
        y: h - 200,
        opacity: 0,
      });

      textTl.to(c, {
        y: 0,
        opacity: 1,
        duration: 0.2,
      });

      if (cPrev) {
        textTl.to(
          cPrev,
          {
            opacity: 0,
            y: -200,
            duration: 0.1,
          },
          '<',
        );
      }

      textTl.to(c, {
        y: 0,
        opacity: 1,
        duration: 1.33,
      });
    }
  }

  async function addClinic() {
    const clinicModel = useClinicModel(threeContext, version, content);

    await clinicModel.load();

    clinicModel.add();

    const plantNames = [
      'Plant',
      'Vase_Room_5002',
      'Vase_room4003',
      'Vase_Room_2001',
      'Vase_Room_5005',
    ];

    const model = threeContext.scene.children.find(
      (child) => child.name === 'clinic-model',
    );
    const scenes = model?.children.filter(
      (child) => child.name === 'Scene',
    ) as THREE.Scene[];

    for (let i = 0; i < scenes.length; i++) {
      const scene = scenes[i];
      const plant = scene.children.filter((child) =>
        plantNames.includes(child.name),
      );
      if (plant && plant.length > 0) {
        // @ts-ignore
        plant[0].material.transparent = true;
        // @ts-ignore
        plant[0].material.alphaTest = 0.48;
      }
    }
  }
}
